import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Progress,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  SaveOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./PlayerLearningPlan.css";

const { Title, Text, Paragraph } = Typography;

const GENERATION_STEPS = [
  { title: "Gathering player profile", description: "Loading player details and roster context" },
  { title: "Analyzing coach feedback", description: "Reviewing recent notes and coaching guidance" },
  { title: "Running performance analytics", description: "Processing trends, grades, and game data" },
  {
    title: "Generating personalized daily messages with AI",
    description: "Crafting your 5-day learning plan",
  },
  {
    title: "Almost done",
    description: "Finalizing your plan — this usually takes 2–3 minutes total",
  },
];

const STEP_ADVANCE_MS = [0, 18000, 40000, 75000, 120000];

const normalizePlanResponse = (payload) => {
  const data = payload?.data?.data || payload?.data || payload;
  if (Array.isArray(data)) return data[0] || null;
  return data || null;
};

const toPlanFormValues = (plan) => {
  const messages = Array.isArray(plan?.messages) ? plan.messages : [];
  const byDay = (day) => messages.find((m) => m.day === day) || {};
  return {
    title: plan?.title || "Learning Plan",
    mainFocusPoint: plan?.mainFocusPoint || "",
    day1: byDay(1).body || "",
    day2: byDay(2).body || "",
    day3: byDay(3).body || "",
    day4: byDay(4).body || "",
    day5: byDay(5).body || "",
    why1: byDay(1).why || "",
    why2: byDay(2).why || "",
    why3: byDay(3).why || "",
    why4: byDay(4).why || "",
    why5: byDay(5).why || "",
  };
};

const toPlanPayload = (values) => ({
  title: values.title,
  mainFocusPoint: values.mainFocusPoint,
  subjectType: "player",
  messages: [1, 2, 3, 4, 5].map((day) => ({
    day,
    body: values[`day${day}`],
    why: values[`why${day}`]?.trim() || null,
  })),
});

const isPlanEmpty = (values, plan) => {
  if (plan?.id) return false;
  if ((values?.mainFocusPoint || "").trim()) return false;
  return ![1, 2, 3, 4, 5].some((day) => (values[`day${day}`] || "").trim());
};

const getPlayerIdFromSearch = (search) => new URLSearchParams(search).get("playerId");
const getPlayerNameFromSearch = (search) =>
  new URLSearchParams(search).get("playerName") || "Player";

const getGenerationProgress = (stepIndex) =>
  Math.min(95, Math.round(((stepIndex + 1) / GENERATION_STEPS.length) * 100));

const GenerationOverlay = ({ stepIndex }) => (
  <div className="learning-plan-gen-overlay" role="dialog" aria-modal="true" aria-busy="true">
    <div className="learning-plan-gen-panel">
      <div className="learning-plan-gen-spinner">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
      </div>
      <Title level={4} style={{ textAlign: "center", marginBottom: 4 }}>
        Generating learning plan with AI
      </Title>
      <Text type="secondary" style={{ display: "block", textAlign: "center", marginBottom: 16 }}>
        Please wait — do not close this tab
      </Text>
      <Progress percent={getGenerationProgress(stepIndex)} status="active" showInfo />
      <Steps
        direction="vertical"
        size="small"
        current={stepIndex}
        style={{ marginTop: 20 }}
        items={GENERATION_STEPS.map((step, i) => ({
          title: step.title,
          description: i === stepIndex ? step.description : undefined,
          status: i < stepIndex ? "finish" : i === stepIndex ? "process" : "wait",
        }))}
      />
      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 20 }}
        message={
          <span className="learning-plan-pulse-reminder" style={{ fontWeight: 600 }}>
            This takes about 2–3 minutes. Keep this window open until generation finishes.
          </span>
        }
        description="We're pulling player info, coach feedback, and performance analytics, then using AI to write five daily messages."
      />
    </div>
  </div>
);

const PlanContent = ({ planValues, generating, onEditDay }) => (
  <div className="learning-plan-content">
    <section className="learning-plan-hero">
      <span className="learning-plan-eyebrow">Weekly focus</span>
      <p className="learning-plan-focus">{planValues.mainFocusPoint}</p>
    </section>

    <section className="learning-plan-days" aria-label="Daily messages">
      {[1, 2, 3, 4, 5].map((day) => (
        <article key={day} className="learning-plan-day">
          <div className="learning-plan-day-marker" aria-hidden>
            {day}
          </div>
          <div className="learning-plan-day-body">
            <p>{planValues[`day${day}`] || "—"}</p>
            {planValues[`why${day}`]?.trim() ? (
              <p className="learning-plan-day-why">
                <span className="learning-plan-why-label">Why it matters</span>
                {planValues[`why${day}`]}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="learning-plan-day-edit"
            onClick={() => onEditDay(day)}
            disabled={generating}
            aria-label={`Edit day ${day}`}
          >
            <EditOutlined />
            <span>Edit</span>
          </button>
        </article>
      ))}
    </section>
  </div>
);

const PlayerLearningPlan = ({
  fetchPlansForPlayer,
  generatePlanDraft,
  createPlan,
  updatePlan,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const playerId = useMemo(() => getPlayerIdFromSearch(location.search), [location.search]);
  const playerName = useMemo(() => getPlayerNameFromSearch(location.search), [location.search]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genStepIndex, setGenStepIndex] = useState(0);
  const [isUnsaved, setIsUnsaved] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [planValues, setPlanValues] = useState(toPlanFormValues({}));
  const [editingDay, setEditingDay] = useState(null);
  const [dayForm] = Form.useForm();
  const genTimersRef = useRef([]);

  const empty = isPlanEmpty(planValues, activePlan);
  const isSaved = Boolean(activePlan?.id) && !isUnsaved;
  const showDraftUi = !empty && isUnsaved;

  const clearGenTimers = useCallback(() => {
    genTimersRef.current.forEach(clearTimeout);
    genTimersRef.current = [];
  }, []);

  const startGenerationProgress = useCallback(() => {
    clearGenTimers();
    setGenStepIndex(0);
    STEP_ADVANCE_MS.slice(1).forEach((delay, i) => {
      genTimersRef.current.push(
        setTimeout(() => setGenStepIndex(i + 1), delay)
      );
    });
  }, [clearGenTimers]);

  useEffect(() => () => clearGenTimers(), [clearGenTimers]);

  useEffect(() => {
    if (!isUnsaved) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isUnsaved]);

  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    fetchPlansForPlayer({
      params: { playerId },
      callback: (ok, response) => {
        setLoading(false);
        if (!ok) {
          message.error("Unable to load learning plan");
          return;
        }
        const plan = normalizePlanResponse(response);
        setActivePlan(plan);
        setPlanValues(toPlanFormValues(plan || {}));
        setIsUnsaved(false);
      },
    });
  }, [playerId]);

  const handleBack = () => {
    if (!isUnsaved) {
      navigate("/player-list");
      return;
    }
    Modal.confirm({
      title: "Leave without saving?",
      icon: <ExclamationCircleOutlined />,
      content:
        "You have an unsaved plan draft. The player will not receive this plan unless you save it first.",
      okText: "Leave anyway",
      okType: "danger",
      cancelText: "Stay and review",
      onOk: () => navigate("/player-list"),
    });
  };

  const handleGenerate = () => {
    if (!playerId || generating) return;
    setGenerating(true);
    startGenerationProgress();
    generatePlanDraft({
      body: {
        subjects: [{ type: "player", ids: [String(playerId)] }],
        scope: { type: "player", ids: [String(playerId)] },
      },
      callback: (ok, response) => {
        clearGenTimers();
        setGenerating(false);
        setGenStepIndex(0);
        if (!ok) {
          message.error("Unable to generate plan draft");
          return;
        }
        const draft = response?.data || response;
        const draftMessages = draft?.messages || [];
        const draftByDay = (day) => draftMessages.find((m) => m.day === day) || {};
        setPlanValues({
          title: "Learning Plan",
          mainFocusPoint: draft?.mainFocusPoint || "",
          day1: draftByDay(1).body || "",
          day2: draftByDay(2).body || "",
          day3: draftByDay(3).body || "",
          day4: draftByDay(4).body || "",
          day5: draftByDay(5).body || "",
          why1: draftByDay(1).why || "",
          why2: draftByDay(2).why || "",
          why3: draftByDay(3).why || "",
          why4: draftByDay(4).why || "",
          why5: draftByDay(5).why || "",
        });
        setIsUnsaved(true);
        message.success({
          content: "Draft ready — review below and click Save Plan to publish to the player.",
          duration: 6,
        });
      },
    });
  };

  const handleSave = () => {
    if (isPlanEmpty(planValues, activePlan)) {
      message.warning("Generate a plan before saving");
      return;
    }
    if (!planValues.mainFocusPoint?.trim()) {
      message.error("Weekly focus point is required");
      return;
    }
    const missingDay = [1, 2, 3, 4, 5].find((day) => !planValues[`day${day}`]?.trim());
    if (missingDay) {
      message.error(`Day ${missingDay} message is required`);
      return;
    }

    setSaving(true);
    const payload = toPlanPayload(planValues);
    const request = activePlan?.id
      ? { action: updatePlan, params: { id: activePlan.id }, body: payload }
      : {
          action: createPlan,
          body: {
            ...payload,
            subjectIds: [String(playerId)],
            campaignType: "five_day_player_learning",
          },
        };

    request.action({
      params: request.params,
      body: request.body,
      callback: (ok, response) => {
        setSaving(false);
        if (!ok) {
          message.error("Unable to save learning plan");
          return;
        }
        const saved = normalizePlanResponse(response);
        setActivePlan(saved);
        setPlanValues(toPlanFormValues(saved || planValues));
        setIsUnsaved(false);
        message.success("Learning plan saved and published to the player");
      },
    });
  };

  const openDayEdit = (day) => {
    setEditingDay(day);
    dayForm.setFieldsValue({
      body: planValues[`day${day}`] || "",
      why: planValues[`why${day}`] || "",
    });
  };

  const saveDayEdit = () => {
    dayForm
      .validateFields()
      .then(({ body, why }) => {
        setPlanValues((prev) => ({
          ...prev,
          [`day${editingDay}`]: body,
          [`why${editingDay}`]: why || "",
        }));
        setIsUnsaved(true);
        setEditingDay(null);
      })
      .catch(() => null);
  };

  const statusTag = showDraftUi ? (
    <Tag color="warning" style={{ marginLeft: 8 }}>
      Draft — not saved
    </Tag>
  ) : isSaved ? (
    <Tag color="success" style={{ marginLeft: 8 }}>
      Saved
    </Tag>
  ) : null;

  if (!playerId) {
    return (
      <div className="w-[98%] p-[2%]">
        <Empty description="Missing player id" />
        <Button className="mt-4" onClick={() => navigate("/player-list")}>
          Back to players
        </Button>
      </div>
    );
  }

  return (
    <>
      {generating && <GenerationOverlay stepIndex={genStepIndex} />}

      <div
        className={`w-[98%] p-[2%] max-w-3xl ${showDraftUi ? "learning-plan-save-bar-spacer" : ""}`}
      >
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          className="px-0 mb-2"
          disabled={generating}
        >
          Back to players
        </Button>

        <div className="flex flex-wrap items-center gap-1 mb-1">
          <Title level={3} style={{ marginBottom: 0 }}>
            Learning Plan
          </Title>
          {statusTag}
        </div>
        <Text type="secondary">{playerName}</Text>

        {loading ? (
          <div className="w-full py-16 text-center">
            <Spin size="large" />
          </div>
        ) : empty ? (
          <Card className="mt-4 text-center">
            <Empty description="No learning plan yet" />
            <Paragraph type="secondary" className="mt-2 mb-4">
              AI will analyze player data and coach feedback, then build a 5-day plan (about 2–3
              minutes).
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<ThunderboltOutlined />}
              loading={generating}
              disabled={generating}
              onClick={handleGenerate}
            >
              Generate with AI
            </Button>
          </Card>
        ) : (
          <div className="mt-6">
            <PlanContent
              planValues={planValues}
              generating={generating}
              onEditDay={openDayEdit}
            />

            <Space className="mt-2" wrap>
              <Button
                icon={<ThunderboltOutlined />}
                loading={generating}
                disabled={generating || saving}
                onClick={handleGenerate}
              >
                Regenerate with AI
              </Button>
              {!showDraftUi && (
                <Button type="primary" loading={saving} icon={<SaveOutlined />} onClick={handleSave}>
                  Save Plan
                </Button>
              )}
            </Space>
          </div>
        )}

        <Modal
          open={editingDay != null}
          title={`Edit Day ${editingDay}`}
          onCancel={() => setEditingDay(null)}
          onOk={saveDayEdit}
          okText="Apply"
          destroyOnClose
        >
          <Form form={dayForm} layout="vertical">
            <Form.Item
              name="body"
              label="Daily message"
              rules={[{ required: true, message: "Day message is required" }]}
            >
              <Input.TextArea rows={4} maxLength={240} showCount />
            </Form.Item>
            <Form.Item name="why" label="Why it matters">
              <Input.TextArea
                rows={4}
                maxLength={600}
                showCount
                placeholder="Why this focus, based on their data (optional for older plans)"
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>

      {showDraftUi && !generating && (
        <div className="learning-plan-save-bar">
          <Text>
            <Text strong>Unsaved draft.</Text> Save to publish this plan to {playerName}.
          </Text>
          <Button
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            Save Plan
          </Button>
        </div>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  fetchPlansForPlayer: (details) =>
    dispatch(global.redux.action.learning_plans.forPlayer(details)),
  generatePlanDraft: (details) =>
    dispatch(global.redux.action.learning_plans.generate(details)),
  createPlan: (details) =>
    dispatch(global.redux.action.learning_plans.create(details)),
  updatePlan: (details) =>
    dispatch(global.redux.action.learning_plans.update(details)),
});

export default connect(null, mapDispatchToProps)(PlayerLearningPlan);
